/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {useAppDispatch, useAppSelector} from "../store";
import {Button, DatePicker, Drawer, Dropdown, Form, Input, InputNumber, Menu, Modal, Result, Space, Spin} from "antd";
import React, {useEffect, useState} from "react";
import {closeDrawer} from "../reducers/drawers-reducer";
import {triggerReload} from "../reducers/reloadsignal-reducer";
import {del, get, patch, post} from "../core/request";
import {IconSet} from "../components/icons";
import {CustomButton} from "../components/buttons";
import {CTExam, MGExam, NMExam, ORMObject, PETExam, RFExam, XRExam} from "../core/datamodel";
import {ViewState} from "../states";
import {ToolTwoTone} from "@ant-design/icons";

const stack_selector = (state: any) => state.drawer.exam
const key = "exam"

interface DrawerProps {
    size?: 'default' | 'large'
}


export function ExamDrawer(props: DrawerProps) {
    const stack = useAppSelector(stack_selector)
    const drawer_stack = useAppSelector(state => state.drawer.stack)
    const dispatch = useAppDispatch();
    const [object, setObject] = useState({} as ORMObject);
    const [form] = Form.useForm();
    const [state, setState] = useState(ViewState.loading);

    const [deviceType, setDeviceType] = useState("CT");
    const [uri, setUri] = useState("");
    const [title, setTitle] = useState("");

    //@ts-ignore
    const isNew = () => !(!!object?.props?.uuid)


    useEffect(() => {
        if (!!stack[stack.length - 1]) {
            setDeviceType(stack[stack.length - 1][0])

            let response_name: string

            switch (deviceType) {
                case "MG":
                    setUri("mg_exams")
                    response_name = "mg_exam"
                    setTitle("MG Exam")
                    break
                case "NM":
                    setUri("nm_exams")
                    response_name = "nm_exam"
                    setTitle("NM Exam")
                    break
                case "PET":
                    setUri("pet_exams")
                    response_name = "pet_exam"
                    setTitle("PET Exam")
                    break
                case "RF":
                    setUri("rf_exams")
                    response_name = "rf_exam"
                    setTitle("RF Exam")
                    break
                case "XR":
                    setUri("xr_exams")
                    response_name = "xr_exam"
                    setTitle("XR Exam")
                    break
                case "CT":
                default:
                    setUri("ct_exams")
                    response_name = "ct_exam"
                    setTitle("CT Exam")
                    break
            }


            if (stack[stack.length - 1][1] !== undefined) {
                setState(ViewState.loading);
                get(response_name + "s/" + stack[stack.length - 1][1]).done((response) => {
                    createObject(response.data[response_name]);
                }).fail(() => setState(ViewState.failed))
            } else {
                createObject({device: {uuid: stack[stack.length - 1][2]}});
            }
        }
    }, [stack, form])

    const createObject = (objectProps: any) => {
        let exam
        switch (deviceType) {
            case "CT":
            default:
                exam = new CTExam(objectProps);
                break
            case "NM":
                exam = new NMExam(objectProps);
                break
            case "MG":
                exam = new MGExam(objectProps);
                break
            case "PET":
                exam = new PETExam(objectProps);
                break
            case "RF":
                exam = new RFExam(objectProps);
                break
            case "XR":
                exam = new XRExam(objectProps);
                break
        }

        form.resetFields()
        form.setFieldsValue({
            ...exam?.props,
        })
        console.debug("Exam", exam, objectProps)
        setObject(exam)
        setState(ViewState.success)
    }

    const onFormFinish = () => {
        let values = form.getFieldsValue()

        object.update({
            ...values,
        })

        let req: JQueryPromise<any>;
        if (isNew()) {
            req = post(uri, object.dump())
        } else {
            req = patch(uri + "/" + object.props.uuid, object.dump())
        }

        req.then(() => {
                dispatch(triggerReload(key))
                dispatch(closeDrawer(key))
            },
            (e) => {
                console.error(e);
            })
    }

    const onDelete = () => {
        Modal.confirm({
            title: "Eintrag wirklich gelöscht werden?",
            content: "Diese Aktion kann nicht rückgängig gemacht werden!",
            maskClosable: true,
            cancelText: 'Abbrechen',
            autoFocusButton: 'cancel',
            okText: 'Löschen',
            okType: 'danger',
            onOk: () => {
                //@ts-ignore
                del(uri + "/" + object.props.uuid)
                    .then(
                        () => {
                            dispatch(triggerReload(key))
                            dispatch(closeDrawer(key))
                        },
                        (e) => {
                            console.error(e);
                        })
                return false;
            }
        })
    }

    const form_layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 16},
    };

    const form_content = () => {
        switch (deviceType) {
            case "CT":
                return (<></>
                )
            default:
                return <></>
        }
    }

    const form_element = (
        <Form
            form={form}
            onFinish={onFormFinish}
            {...form_layout}
            requiredMark={true}
        >
            <Form.Item label="study_date" name="study_date">
                <DatePicker name="study_date" showTime allowClear/>
            </Form.Item>

            <Form.Item label="study_description" name="study_description">
                <Input name="study_description" placeholder="study_description"/>
            </Form.Item>

            {/*todo selctor for gender */}
            <Form.Item label="pat_gender" name="pat_gender">
                <Input name="pat_gender" placeholder="pat_gender"/>
            </Form.Item>

            {/*todo switch for adult */}
            <Form.Item label="pat_is_adult" name="pat_is_adult">
                <Input name="pat_is_adult" placeholder="pat_is_adult"/>
            </Form.Item>

            <Form.Item label="pat_bmi" name="pat_bmi_db">
                <InputNumber decimalSeparator={","} name="pat_bmi_db" placeholder="pat_bmi"/>
            </Form.Item>

            <Form.Item label="pat_height" name="pat_height">
                <InputNumber decimalSeparator={","} name="pat_height" placeholder="pat_height"/>
            </Form.Item>

            <Form.Item label="pat_weight" name="pat_weight">
                <InputNumber decimalSeparator={","} name="pat_weight" placeholder="pat_weight"/>
            </Form.Item>

            <Form.Item label="pat_age" name="pat_age">
                <InputNumber decimalSeparator={","} name="pat_age" placeholder="pat_age"/>
            </Form.Item>

            <Form.Item label="comment" name="comment">
                <Input name="comment" placeholder="comment"/>
            </Form.Item>

            <Form.Item label="eff_dose" name="eff_dose">
                <InputNumber decimalSeparator={","} name="eff_dose" placeholder="eff_dose"/>
            </Form.Item>

            {form_content()}

            {/* Buttons */}
            <Form.Item
                label=" "
                colon={false}
            >
                <Form.Item
                    style={{display: 'inline-block'}}
                >
                    <Button type="primary" htmlType="submit">
                        <IconSet type='save'/>&nbsp;Speichern
                    </Button>
                </Form.Item>
                <Form.Item
                    style={{display: 'inline-block', margin: '0 24px'}}
                >
                    <Button onClick={() => dispatch(closeDrawer(key))}>
                        <IconSet type='cancel'/>&nbsp;Änderungen verwerfen
                    </Button>
                </Form.Item>
            </Form.Item>
        </Form>
    )

    const menu = (
        isNew() ? (<></>) : (
            <Dropdown
                arrow
                overlay={
                    <Menu style={{padding: 12}} items={[
                        {
                            key: "delete",
                            danger: true,
                            onClick: onDelete,
                            label: <><IconSet type='delete'/> Löschen</>
                        },
                    ]}
                    />
                }
                placement="bottomRight"
                className='button-edit'
            >
                <CustomButton mode='more'/>
            </Dropdown>)
    )


    const stateSwitch = () => {
        switch (state) {
            case ViewState.initial:
            case ViewState.loading:
                return <Spin spinning>
                    {form_element}
                </Spin>

            case ViewState.failed:
                return <>
                    <Result
                        status="404"
                        title="Fehler"
                        subTitle="Entschuldigung, das Event konnten nicht geladen werden!"
                    />
                    <div style={{display: "none"}}>
                        {form_element}
                    </div>
                </>

            case ViewState.success:
                return form_element
        }
    }

    const construction = (
        <>
            <Result
                icon={<ToolTwoTone/>}
                title='An dieser Seite wird derzeit noch gearbeitet.'
            />
            <div style={{display: "none"}}>
                {form_element}
            </div>
        </>
    )


    return (
        <Drawer
            open={stack.length > 0 && drawer_stack[drawer_stack.length - 1] === key}
            size={props.size}
            onClose={() => dispatch(closeDrawer(key))}
            forceRender={true}
            title={
                <Space direction="horizontal" style={{width: '100%', justifyContent: 'center'}}>
                    <h1 style={{marginBottom: 0}}>{title + (isNew() ? " hinzufügen" : " bearbeiten")}</h1>
                </Space>
            }
            extra={menu}
            zIndex={drawer_stack[drawer_stack.length - 1] === key ? 1000 : 900}
        >
            {construction /* TODO stateSwitch()*/}
        </Drawer>
    )
}


ExamDrawer.defaultProps = {
    size: 'large'
}